import React, { useEffect, useState } from 'react';
import { auth, adminBonuses, bonuses, PAYING_COMPANY_OPTIONS } from './api';

// ===========================================================================
// One-time bonuses (InsureDocs v5.9.0)
//
// Admin-only entry modal + history, and the producer's read-only section.
// A one-time bonus is created already paid and is never part of a payout
// batch. Corrections are by VOID only (reason required) — no edit, no delete.
// Display names come from the API (paying_company_name); the only local
// labels are the PAYING_COMPANY_OPTIONS select options.
// v5.11.0: a one-time bonus is one PAYMENT with one or more LINES (negative
// lines allowed, never 0). The server computes the total (= sum of the lines,
// must be above $0) and the description (line descriptions joined with "; ").
// ===========================================================================

// Match the formatting helpers used in Bonuses.jsx / PayoutBatches.jsx.
const fmtMoney = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};
const fmtDate = (d) => {
  if (!d) return '—';
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString();
  return new Date(d).toLocaleDateString();
};
const fmtDateTime = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return String(d); }
};

// v5.11.0: a line amount as integer cents, or null if not a valid non-zero
// amount with at most 2 decimals (negative allowed). Cents avoid float drift
// in the live total.
const lineCents = (v) => {
  const t = String(v || '').trim();
  if (!/^-?\d+(\.\d{1,2})?$/.test(t)) return null;
  const neg = t.startsWith('-');
  const [w, f = ''] = t.replace('-', '').split('.');
  const cents = Number(w) * 100 + Number((f + '00').slice(0, 2));
  return cents === 0 ? null : (neg ? -cents : cents);
};
const emptyLine = () => ({ description: '', amount: '' });

// v5.11.0: a payment's lines (line_no · description · amount), read-only.
// Negative amounts are shown in red. Falls back to the payment's own
// description/amount for a response without lines.
export function OneTimeBonusLines({ item, textPrimary, textSecondary, borderColor, style = {} }) {
  const lines = item?.lines?.length
    ? item.lines
    : [{ line_no: 1, description: item?.description, amount: item?.amount }];
  return (
    <div style={{ fontSize: 12, ...style }}>
      {lines.map(l => (
        <div key={l.line_no} style={{ display: 'grid', gridTemplateColumns: '22px 1fr 110px', gap: 6, padding: '3px 0', borderTop: borderColor ? `1px dashed ${borderColor}` : undefined }}>
          <span style={{ color: textSecondary }}>{l.line_no}</span>
          <span style={{ color: textPrimary, wordBreak: 'break-word' }}>{l.description}</span>
          <span style={{ textAlign: 'right', fontWeight: 600, color: Number(l.amount) < 0 ? '#e0a8a8' : textPrimary }}>{fmtMoney(l.amount)}</span>
        </div>
      ))}
    </div>
  );
}

// Today's date in Central time as YYYY-MM-DD (the agency's calendar day).
const todayCentral = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date());

// Readable message from an axios error — FastAPI 422 detail lists included.
function errorMessage(e) {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map(d => {
      const field = Array.isArray(d.loc) ? d.loc.filter(x => x !== 'body').join('.') : '';
      const msg = String(d.msg || JSON.stringify(d)).replace(/^Value error, /, '');
      return field ? `${field}: ${msg}` : msg;
    }).join('\n');
  }
  return detail || e?.message || 'Unknown error';
}

// Void flow shared by the history list and the detail modal (admin only).
// Prompts for a REQUIRED reason; returns true only if the void succeeded.
async function promptAndVoidOneTime(r) {
  const reason = window.prompt(`Void one-time bonus #${r.id} (${r.staff_name}, ${fmtMoney(r.amount)})?\n\nReason (required):`);
  if (reason === null) return false;           // cancelled
  if (!reason.trim()) { alert('A reason is required to void.'); return false; }
  try {
    await adminBonuses.oneTime.void(r.id, reason.trim());
    return true;
  } catch (e) {
    alert('Void failed: ' + errorMessage(e));
    return false;
  }
}

// Users who receive bonuses — INCLUDING inactive ones (labelled), active first.
function useBonusProducers() {
  const [producers, setProducers] = useState([]);
  useEffect(() => {
    auth.listVisibleUsers()
      .then(r => setProducers((r.data?.users || []).filter(u => u.receives_bonuses)))
      .catch(() => setProducers([]));
  }, []);
  return producers;
}
const producerLabel = (u) => u.full_name + (u.is_active ? '' : ' (inactive)');

// ---------------------------------------------------------------------------
// Entry modal (admin only)
// ---------------------------------------------------------------------------

export function OneTimeBonusModal({ darkMode, onClose, onSaved }) {
  const producers = useBonusProducers();
  const [form, setForm] = useState({
    staff_user_id: '',
    paid_date: todayCentral(),
    paying_company: '',           // NO default — must be chosen
    payment_method: 'check',
    check_number: '',
    zelle_note: '',
  });
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setLine = (i, k, v) => setLines(ls => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const addLine = () => setLines(ls => [...ls, emptyLine()]);
  const removeLine = (i) => setLines(ls => (ls.length > 1 ? ls.filter((_, j) => j !== i) : ls));

  const lineCentsList = lines.map(l => lineCents(l.amount));
  const linesOk = lines.length > 0 && lines.every((l, i) => l.description.trim() && lineCentsList[i] !== null);
  const totalCents = lineCentsList.reduce((s, c) => s + (c || 0), 0);
  const missing = [];
  if (!form.staff_user_id) missing.push('producer');
  if (!linesOk) missing.push('a description and a non-zero amount (max 2 decimals) on every line');
  else if (totalCents <= 0) missing.push('a total above $0');
  if (!form.paid_date) missing.push('the paid date');
  if (!form.paying_company) missing.push('the paying company');
  if (form.payment_method === 'check' && !form.check_number.trim()) missing.push('the check #');
  const valid = missing.length === 0;

  const save = async () => {
    if (!valid || saving) return;
    setSaving(true);
    setError('');
    try {
      const isCheck = form.payment_method === 'check';
      await adminBonuses.oneTime.create({
        staff_user_id: Number(form.staff_user_id),
        lines: lines.map(l => ({ description: l.description.trim(), amount: l.amount.trim() })),
        paid_date: form.paid_date,
        paying_company: form.paying_company,
        payment_method: form.payment_method,
        check_number: isCheck ? form.check_number.trim() : null,
        zelle_note: !isCheck && form.zelle_note.trim() ? form.zelle_note.trim() : null,
      });
      onSaved && onSaved();
    } catch (e) {
      setError(errorMessage(e));
    }
    setSaving(false);
  };

  const bg = darkMode ? '#1a1a1a' : '#f5f5f5';
  const panelBg = darkMode ? '#242424' : '#fff';
  const borderColor = darkMode ? '#333' : '#e5e5e5';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';
  const label = { fontSize: 11, color: textSecondary, marginBottom: 3 };
  const input = { width: '100%', padding: '7px 9px', fontSize: 13, border: `1px solid ${borderColor}`, borderRadius: 5, background: bg, color: textPrimary, boxSizing: 'border-box', fontFamily: 'inherit' };
  const req = <span style={{ color: '#e0a8a8' }}>*</span>;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div style={{ background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 8, width: 520, maxWidth: '94vw', maxHeight: '92vh', overflowY: 'auto', padding: 20, boxSizing: 'border-box' }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>One-time bonus</div>
        <div style={{ fontSize: 12, color: textSecondary, marginBottom: 14 }}>
          Recorded as already paid. It never joins a payout batch. To correct it later, void it and enter it again.
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={label}>Producer {req}</div>
          <select value={form.staff_user_id} onChange={e => set('staff_user_id', e.target.value)} style={input}>
            <option value="" disabled>— Choose producer —</option>
            {producers.map(u => <option key={u.id} value={u.id}>{producerLabel(u)}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={label}>Lines {req} <span style={{ fontSize: 10 }}>(a negative amount is allowed, e.g. a deduction; never 0)</span></div>
          {lines.map((l, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 28px', gap: 6, marginBottom: 6 }}>
              <input type="text" value={l.description} placeholder={i === 0 ? 'e.g. health insurance bonus' : 'Description'}
                onChange={e => setLine(i, 'description', e.target.value)} style={input} />
              <input type="text" inputMode="decimal" value={l.amount} placeholder="e.g. 2200.00"
                onChange={e => setLine(i, 'amount', e.target.value.replace(/[$,\s]/g, ''))}
                style={{ ...input, textAlign: 'right', color: lineCentsList[i] !== null && lineCentsList[i] < 0 ? '#e0a8a8' : textPrimary }} />
              <button type="button" onClick={() => removeLine(i)} disabled={lines.length === 1} title="Remove line"
                style={{ border: `1px solid ${borderColor}`, borderRadius: 5, background: panelBg, color: textSecondary, cursor: lines.length === 1 ? 'not-allowed' : 'pointer', opacity: lines.length === 1 ? 0.4 : 1 }}>✕</button>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 2 }}>
            <button type="button" onClick={addLine}
              style={{ padding: '4px 10px', border: `1px solid ${borderColor}`, borderRadius: 5, background: panelBg, color: textPrimary, fontSize: 12, cursor: 'pointer' }}>
              + Add line
            </button>
            <span style={{ fontSize: 13, fontWeight: 700, color: totalCents > 0 ? textPrimary : '#e0a8a8' }}>
              Total {fmtMoney(totalCents / 100)}
            </span>
          </div>
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={label}>Paid date {req}</div>
          <input type="date" value={form.paid_date} onChange={e => set('paid_date', e.target.value)} style={{ ...input, maxWidth: 200 }} />
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={label}>Paying company {req}</div>
          <select value={form.paying_company} onChange={e => set('paying_company', e.target.value)}
            style={{ ...input, borderColor: form.paying_company ? borderColor : '#f5c97a' }}>
            <option value="" disabled>— Choose the paying company —</option>
            {PAYING_COMPANY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10, marginBottom: 12 }}>
          <div>
            <div style={label}>Method {req}</div>
            <select value={form.payment_method} onChange={e => set('payment_method', e.target.value)} style={input}>
              <option value="check">Check</option>
              <option value="zelle">Zelle</option>
            </select>
          </div>
          {form.payment_method === 'check' ? (
            <div>
              <div style={label}>Check # {req}</div>
              <input type="text" maxLength={50} value={form.check_number}
                onChange={e => set('check_number', e.target.value)} style={input} />
            </div>
          ) : (
            <div>
              <div style={label}>Zelle note (optional)</div>
              <input type="text" maxLength={255} value={form.zelle_note}
                onChange={e => set('zelle_note', e.target.value)} style={input} />
            </div>
          )}
        </div>

        {error && (
          <div style={{ padding: '8px 10px', marginBottom: 12, borderRadius: 5, background: darkMode ? '#5a1a1a' : '#fdecec', color: darkMode ? '#e0a8a8' : '#a11', fontSize: 12, whiteSpace: 'pre-wrap' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
          {!valid && (
            <span style={{ fontSize: 11, color: '#f5c97a', marginRight: 'auto' }}>Still needed: {missing.join(', ')}.</span>
          )}
          <button onClick={onClose} disabled={saving}
            style={{ padding: '7px 16px', border: `1px solid ${borderColor}`, borderRadius: 5, background: panelBg, color: textSecondary, fontSize: 13, cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={save} disabled={!valid || saving}
            style={{ padding: '7px 16px', border: 'none', borderRadius: 5, fontSize: 13, fontWeight: 600, color: '#fff',
              background: valid ? '#185FA5' : '#444', cursor: valid && !saving ? 'pointer' : 'not-allowed' }}>
            {saving ? 'Saving…' : 'Save one-time bonus'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// History (admin only) — dashboard section
// ---------------------------------------------------------------------------

export function OneTimeBonusHistory({ darkMode, reloadKey, onChanged, bg, panelBg, borderColor, textPrimary, textSecondary }) {
  const producers = useBonusProducers();
  const now = new Date();
  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) years.push(y);

  const [employeeId, setEmployeeId] = useState('');
  const [year, setYear] = useState(String(now.getFullYear()));
  const [showVoided, setShowVoided] = useState(true);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [voidingId, setVoidingId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = { include_voided: showVoided };
      if (employeeId) params.employee_id = employeeId;
      if (year) params.year = year;
      const res = await adminBonuses.oneTime.list(params);
      setRows(res.data || []);
    } catch (e) {
      setError(errorMessage(e));
      setRows([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [employeeId, year, showVoided, reloadKey]);

  const voidRow = async (r) => {
    setVoidingId(r.id);
    if (await promptAndVoidOneTime(r)) {
      await load();
      onChanged && onChanged();
    }
    setVoidingId(null);
  };

  const live = rows.filter(r => !r.is_voided);
  const liveTotal = live.reduce((s, r) => s + Number(r.amount || 0), 0);
  const cols = '90px 1.1fr 1.6fr 1.3fr 70px 1fr 100px 1fr 1.3fr';
  const inputStyle = { padding: '6px 10px', fontSize: 12, border: `1px solid ${borderColor}`, borderRadius: 5, background: panelBg, color: textPrimary };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <select value={employeeId} onChange={e => setEmployeeId(e.target.value)} style={{ ...inputStyle, minWidth: 180 }}>
          <option value="">All producers</option>
          {producers.map(u => <option key={u.id} value={u.id}>{producerLabel(u)}</option>)}
        </select>
        <select value={year} onChange={e => setYear(e.target.value)} style={inputStyle}>
          <option value="">All years</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <label style={{ fontSize: 12, color: textPrimary, display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={showVoided} onChange={e => setShowVoided(e.target.checked)} />
          Show voided
        </label>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: textSecondary }}>
          {live.length} paid · <strong style={{ color: textPrimary }}>{fmtMoney(liveTotal)}</strong>
        </span>
      </div>

      <div style={{ border: `1px solid ${borderColor}`, borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: cols, padding: '8px 12px', background: bg, fontSize: 10, color: textSecondary, fontWeight: 600, textTransform: 'uppercase', gap: 6 }}>
          <span>Paid date</span><span>Producer</span><span>Description</span><span>Company</span>
          <span>Method</span><span>Check # / note</span><span style={{ textAlign: 'right' }}>Amount</span>
          <span>Created by</span><span>Status</span>
        </div>
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: textSecondary, fontSize: 12 }}>Loading...</div>
        ) : error ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#e0a8a8', fontSize: 12, whiteSpace: 'pre-wrap' }}>{error}</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: textSecondary, fontSize: 12 }}>No one-time bonuses.</div>
        ) : rows.map(r => {
          const strike = r.is_voided ? { textDecoration: 'line-through' } : {};
          return (
            <div key={r.id} style={{ display: 'grid', gridTemplateColumns: cols, padding: '9px 12px', borderTop: `1px solid ${borderColor}`, fontSize: 12, alignItems: 'center', gap: 6,
              color: r.is_voided ? textSecondary : textPrimary, opacity: r.is_voided ? 0.7 : 1 }}>
              <span>{fmtDate(r.paid_date)}</span>
              <span>{r.staff_name || `user #${r.staff_user_id}`}</span>
              <span style={{ ...strike, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.description}>{r.description}</span>
              <span>{r.paying_company_name}</span>
              <span>{r.payment_method === 'check' ? 'Check' : 'Zelle'}</span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={r.payment_method === 'check' ? (r.check_number || '') : (r.zelle_note || '')}>
                {r.payment_method === 'check' ? (r.check_number ? `#${r.check_number}` : '—') : (r.zelle_note || '—')}
              </span>
              <span style={{ ...strike, textAlign: 'right', fontWeight: 600 }}>{fmtMoney(r.amount)}</span>
              <span>{r.created_by_name || `user #${r.created_by_id}`}</span>
              <span>
                {r.is_voided ? (
                  <span title={`Voided ${fmtDateTime(r.voided_at)} by ${r.voided_by_name || ''}: ${r.void_reason || ''}`}>
                    <span style={{ padding: '2px 7px', borderRadius: 3, fontSize: 10, fontWeight: 700, background: '#5a1a1a', color: '#e0a8a8' }}>VOIDED</span>
                    <div style={{ fontSize: 11, marginTop: 3 }}>{r.void_reason}</div>
                    <div style={{ fontSize: 10 }}>{r.voided_by_name || '—'} · {fmtDateTime(r.voided_at)}</div>
                  </span>
                ) : (
                  <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ padding: '2px 7px', borderRadius: 3, fontSize: 10, fontWeight: 700, background: '#1a3a1a', color: '#a8d8a8' }}>PAID</span>
                    <button onClick={() => voidRow(r)} disabled={voidingId === r.id}
                      style={{ padding: '3px 9px', fontSize: 11, border: `1px solid ${borderColor}`, borderRadius: 4, background: panelBg, color: '#e0a8a8', cursor: 'pointer' }}>
                      {voidingId === r.id ? 'Voiding…' : 'Void'}
                    </button>
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Read-only detail modal (v5.9.1, Payout batches page). Same fields as the
// admin history row; admins get Void (required reason) when not yet voided.
// ---------------------------------------------------------------------------

export function OneTimeBonusDetail({ item, isAdmin, darkMode, onClose, onVoided }) {
  const [voiding, setVoiding] = useState(false);
  if (!item) return null;
  const r = item;

  const panelBg = darkMode ? '#242424' : '#fff';
  const borderColor = darkMode ? '#333' : '#e5e5e5';
  const textPrimary = darkMode ? '#f0f0f0' : '#1a1a1a';
  const textSecondary = darkMode ? '#aaa' : '#666';
  const strike = r.is_voided ? { textDecoration: 'line-through' } : {};
  const isCheck = r.payment_method === 'check';

  const field = (label, value, style = {}) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: textSecondary, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, color: textPrimary, wordBreak: 'break-word', ...style }}>{value}</div>
    </div>
  );

  const doVoid = async () => {
    setVoiding(true);
    const ok = await promptAndVoidOneTime(r);
    setVoiding(false);
    if (ok) onVoided && onVoided();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget && !voiding) onClose(); }}>
      <div style={{ background: panelBg, color: textPrimary, border: `1px solid ${borderColor}`, borderRadius: 8, width: 480, maxWidth: '94vw', maxHeight: '92vh', overflowY: 'auto', padding: 20, boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 16, fontWeight: 700 }}>One-time bonus #{r.id}</span>
          {r.is_voided
            ? <span style={{ padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 700, background: '#5a1a1a', color: '#e0a8a8' }}>VOIDED</span>
            : <span style={{ padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 700, background: '#1a3a1a', color: '#a8d8a8' }}>PAID</span>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 16 }}>
          {field('Paid date', fmtDate(r.paid_date))}
          {field('Producer', r.staff_name || `user #${r.staff_user_id}`)}
          <div style={{ gridColumn: '1 / 3' }}>
            {field(`Lines (${(r.lines || []).length || 1})`,
              <OneTimeBonusLines item={r} textPrimary={textPrimary} textSecondary={textSecondary} borderColor={borderColor} style={strike} />)}
          </div>
          {field('Paying company', r.paying_company_name)}
          {field('Total', fmtMoney(r.amount), { fontWeight: 700, ...strike })}
          {field('Method', isCheck ? 'Check' : 'Zelle')}
          {field(isCheck ? 'Check #' : 'Zelle note', isCheck ? (r.check_number ? `#${r.check_number}` : '—') : (r.zelle_note || '—'))}
          {field('Created by', r.created_by_name || `user #${r.created_by_id}`)}
          {field('Created', fmtDateTime(r.created_at))}
        </div>

        {r.is_voided && (
          <div style={{ padding: '8px 10px', margin: '4px 0 12px', borderRadius: 5, background: darkMode ? '#2a1414' : '#fdecec', color: darkMode ? '#e0a8a8' : '#a11', fontSize: 12 }}>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>VOIDED</div>
            <div>{r.void_reason}</div>
            <div style={{ fontSize: 11, marginTop: 2 }}>{r.voided_by_name || '—'} · {fmtDateTime(r.voided_at)}</div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {isAdmin && !r.is_voided && (
            <button onClick={doVoid} disabled={voiding}
              style={{ padding: '7px 14px', border: `1px solid ${borderColor}`, borderRadius: 5, background: panelBg, color: '#e0a8a8', fontSize: 13, cursor: voiding ? 'not-allowed' : 'pointer', marginRight: 'auto' }}>
              {voiding ? 'Voiding…' : 'Void'}
            </button>
          )}
          <button onClick={onClose} disabled={voiding}
            style={{ padding: '7px 16px', border: 'none', borderRadius: 5, background: '#185FA5', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Producer's own one-time bonuses (read-only, MyBonuses). Hidden when empty.
// ---------------------------------------------------------------------------

export function MyOneTimeBonuses({ panelBg, borderColor, textPrimary, textSecondary }) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    bonuses.mineOneTime()
      .then(r => setRows(r.data || []))
      .catch(() => setRows([]));
  }, []);

  if (rows.length === 0) return null;
  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const cols = '110px 1fr 1fr 120px';

  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: textPrimary, marginBottom: 8 }}>One-time bonuses</div>
      <div style={{ border: `1px solid ${borderColor}`, borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: cols, padding: '8px 12px', background: panelBg, fontSize: 10, color: textSecondary, fontWeight: 600, textTransform: 'uppercase', gap: 6 }}>
          <span>Paid date</span><span>Description</span><span>Company</span><span style={{ textAlign: 'right' }}>Amount</span>
        </div>
        {rows.map(r => (
          <div key={r.id} style={{ display: 'grid', gridTemplateColumns: cols, padding: '9px 12px', borderTop: `1px solid ${borderColor}`, fontSize: 12, color: textPrimary, gap: 6 }}>
            <span>{fmtDate(r.paid_date)}</span>
            {(r.lines || []).length > 1
              ? <OneTimeBonusLines item={r} textPrimary={textPrimary} textSecondary={textSecondary} />
              : <span>{r.description}</span>}
            <span>{r.paying_company_name}</span>
            <span style={{ textAlign: 'right', fontWeight: 600 }}>{fmtMoney(r.amount)}</span>
          </div>
        ))}
        <div style={{ display: 'grid', gridTemplateColumns: cols, padding: '9px 12px', borderTop: `1px solid ${borderColor}`, fontSize: 12, fontWeight: 700, color: textPrimary, gap: 6, background: panelBg }}>
          <span>Total</span><span /><span />
          <span style={{ textAlign: 'right' }}>{fmtMoney(total)}</span>
        </div>
      </div>
    </div>
  );
}
